/* jshint esversion: 6, -W097 */
/* globals module */
'use strict';

/**
 * Creates a service for abstracting basic get/fetch functionality used to
 * reduce redundant requests, but still allow for forcing re-requests of data.
 *
 * @module enervee_utils.get_fetch
 */
GetFetchService.$inject = [
    '$http',
    '$log',
    '$q',
    'ProductApiConsumerService',
    'StorageService',
];
function GetFetchService(
    $http,
    $log,
    $q,
    ProductApiConsumerService,
    StorageService
) {
    var _pub = {};
    var dataPath = '_getFetch.data';
    var promisesPath = '_getFetch.promises';

    // Main functions

    /**
     * Get a stored resource if it exists. If the resource doesn't exist
     * (or if `options.force` is truthy), then request the resource,
     * store the resulting response data, and resolve with that data.
     *
     * @param {String|String[]} path - the path related to where the resource's data
     * is to be stored.
     * @param {String|Object} requestOptions - either a url string for
     * the resource or and object with a `url` string property and `params` or `data`
     * object property.
     * @param {String} [requestOptions.method="GET"]
     * @param {String} [requestOptions.url]
     * @param {Object} [requestOptions.params]
     * @param {Object} [requestOptions.data]
     * @param {Object} [requestOptions.requestingService]
     * @param {Object} [options] - Further options. See also: `_getNestedValue`,
     * `fetch`.
     * @param {Boolean} [options.force] - whether or not previous requests
     * for this data should be ignored.
     * @param {Function} [options.responseReturnFunction] - The function
     * for returning a value from the requeset for data that should be
     * stored for this resource.
     * @param {Function} [options.preFetchCheck] - An optional function
     * for determining if the fetch should actually be run. If the function
     * exists and returns false, the fetch method just returns an empty,
     * immediately-resolving promise.
     *
     * @example
     * // Get a single resource and store the result
     * function getUserProfile(force) {
     *   var url = 'api/1.0/user-profile/';
     *   return GetFetchService.get('userProfile', url, {force: force});
     * }
     * 
     * @example
     * // Get a resource by its 'pk' (or 'id', in this case).
     * function getProduct(productId, force) {
     *   var url = 'api/1.0/products/' + productId + '/';
     *   return GetFetchService.get(['product', productId], url, {force: force});
     * };
     * 
     * @example
     * // Do a logic check before sending a request for data (which could also be done in the enclosing method)
     * function getCurrentUser(force) {
     *   return GetFetchService.get('currentUserInfo', 'api/1.0/profile-info/', {
     *     force: force,
     *     preFetchCheck: function () {
     *       return _pub.isAuthenticated();
     *     }
     *   });
     * };
     * 
     * @example
     * // Perform logic to store a different part of the request response other than `request.data`
     * function getUserUtilityRate(force) {
     *   return GetFetchService.get('userUtilityRate', 'api/1.0/user-profile/electrical-utility/', {
     *     force: force,
     *     responseReturnFunction: function (response) {
     *       if (response.data.electrical_rate !== null) {
     *         return response.data.electrical_rate;
     *       } else if (response.data.user_set_electrical_rate !== null) {
     *         return response.data.user_set_electrical_rate;
     *       }
     *     }
     *   });
     * }
     * 
     * @example
     * // Use a custom function instead of sending a request
     * function getSomeCustomData(arg, force) {
     *   return GetFetchService.get(
     *     'customData',
     *     () => $timeout(1000).then(() => getAsyncData(arg)),
     *     {force: force}
     *   );
     * }
     */

    _pub.get = function (path, requestOptions, options) {
        options = options || {};
        if (getData(path, options) === undefined || options.force) {
            // If the data doesn't exist yet or force is true, run
            // fetch, then resolve after it has finished
            return fetch(path, requestOptions, options).then(
                function fetchSuccess() {
                    return getData(path, options);
                }, function fetchError(error) {
                    return $q.reject(error);
                }
            );
        }
        // Otherwise, resolve with the data immediately
        return $q.resolve(getData(path, options));
    };

    /**
     * Update a stored resource with data from an arbitrary source
     *
     * @param {String|String[]} path - the path related to where the resource's data is to be stored.
     * @param {*} value - the new value to be stored for the provided path
     * @param {Object} [options] - See also: `_setNestedValue`.
     *
     * @example
     * // Update a stored resource with data from another source
     * var = saveEmailSubscriptionInfo = function (emailSettings) {
     *   return $http.post('api/1.0/email/subscription/', emailSettings).then(function (response) {
     *     GetFetchService.update('emailSubscriptionInfo', response.data);
     *   });
     * };
     */
    _pub.update = function (path, value, options) {
        defaultFetchSetter(path, value, options);
    };

    /**
     * Clears a stored resource, setting the value to `undefined` (creating the path if it
     * doesn't exist).
     * 
     * @example
     * // Update a stored resource with data from another source
     * GetFetchService.update('clearableValue', 'test value');
     * console.log(GetFetchService.getRaw('clearableValue'));
     * // > 'test value'
     * GetFetchService.clear('clearableValue');
     * console.log(GetFetchService.getRaw('clearableValue'));
     * // > undefined
     */
    _pub.clear = function (path) {
        StorageService.clear([dataPath, path]);
    };

    /**
     * Get a raw stored resource, even if its undefined
     *
     * @param {String|String[]} path - the path related to where the resource's data is to be stored.
     * @param {*} value - the new value to be stored for the provided path
     * @param {Object} [options] - See also: `_setNestedValue`.
     */
    _pub.getRaw = function (path, options) {
        return getData(path, options);
    };


    // Helper functions

    /**
     * Helper function to convert _either_ an object or a string into
     * an object, defaulting the method to GET.
     *
     * @param {String|Object} options - either a url string or an object
     * with a `url` string property
     * @param {String} options.url - The url that the request should be
     * made to.
     * @param {String} [options.method='GET'] - The method of the HTTP
     * request to be used. Defaults to 'GET'.
     * @returns {Object}
     */
    function normalizeRequestOptions(options) {
        if (angular.isFunction(options)) return options;
        if (typeof options === "string") {
            var url = options;
            options = { url: url };
        }
        options.method = options.method || 'GET';
        if (!options.url) {
            $log.warn("No URL provided for request options!");
        }
        return options;
    }

    /**
     * Helper function that determines how the data returned from a
     * fetch request should be saved in a resource.
     *
     * @param {String|String[]} path - the path related to where the resource's
     * data is to be stored.
     * @param {Object} data - the data resulting from the fetch request.
     * @param {Object} [options] - Further options. See `_setNestedValue`
     * for all options.
     */
    function defaultFetchSetter(path, data, options) {
        setData(path, data, options);
    }

    /**
     * Helper function that is the default function for determining
     * which data from a request should be saved for a resource
     *
     * @param {Object} response - the response from an HTTP request
     * @returns {*} The value that should be set for a particular
     * resource
     */
    function defaultResponseReturnFunction(response) {
        return response.data;
    }

    /**
     * @param {string|string[]} path - the path related to where the resource's data
     * is to be stored.
     * @param {string|Object|Function} requestOptions - Can be a url string for
     * the resource, an object with a `url` string property and `params`
     * object property, or a function that is called if a resource needs to be fetched.
     * @param {string} [requestOptions.method='GET']
     * @param {string} [requestOptions.url]
     * @param {Object} [requestOptions.params]
     * @param {Object} [requestOptions.data]
     * @param {Object} [options] - Further options
     * @param {Boolean} [options.force] - whether or not previous requests
     * for this data should be ignored.
     * @param {Function} [options.responseReturnFunction] - An optional
     * function for returning a value from the requeset for data that should
     * be stored for this resource.
     * @param {Function} [options.preFetchCheck] - An optional function
     * for determining if the fetch should actually be run. If the function
     * exists and returns false, the fetch method just returns an empty,
     * immediately-resolving promise.
     */
    function fetch(path, requestOptions, options) {
        options = options || {};
        if (angular.isFunction(options.preFetchCheck) && !options.preFetchCheck()) {
            // If a pre-fetch function exists and returns false, skip
            // the normal fetch and just return an empty promise
            return $q.reject({error: 'GetFetchService Error: options.prefetchCheck returned false.'});
        }
        // Normalize the requestOptions to always be an object, even if
        // just a string was passed for the url
        requestOptions = normalizeRequestOptions(requestOptions);
        if (!getPromise(path, options) || options.force) {
            // If the promise doesn't exist yet or force is true,
            // create a new request
            let promise;
            let responseReturnFunction = options.responseReturnFunction || defaultResponseReturnFunction;
            if (angular.isFunction(requestOptions)) {
                promise = $q.resolve(requestOptions());
                // If we're using a custom fetch method, we will assume that the method returns
                // the data, not a response object.
                responseReturnFunction = (data) => data;
            } else {
                let url = requestOptions.url;

                if (!/^https?:\/\//i.test(url)) {
                    url = ProductApiConsumerService.getFullUrl(url);
                }

                let requestingService = requestOptions.requestingService || $http;

                promise = requestingService({
                    method: requestOptions.method,
                    url: url,
                    params: requestOptions.params,
                    data: requestOptions.data,
                });
            }
            promise = promise.then(function (response) {
                // If no responseReturnFunction is specified use the default
                // Use the return value from the responseReturnFunction
                // as the new value for the resource
                return $q.resolve(responseReturnFunction(response)).then((data) => {
                    defaultFetchSetter(path, data, options);
                    clearPromise(path);
                });
            }, function (error) {
                clearPromise(path);
                return $q.reject(error);
            });
            setPromise(path, promise);
        }
        return getPromise(path, options);
    }

    /**
     * Helper function to shortcut getting values in StorageService's `_getFetch.data`
     *
     * @param {String|String[]} path
     * @param {Object} options See options for `StorageService.get`.
     * @return {*}
     */
    function getData(path, options) {
        return StorageService.get([dataPath, path], options);
    }

    /**
     * Helper function to shortcut setting values in StorageService's `_getFetch.data`
     *
     * @param {String|String[]} path
     * @param {*} data
     * @return {*}
     */
    function setData(path, data) {
        return StorageService.set([dataPath, path], data);
    }

    /**
     * Helper function to shortcut getting values in StorageService's `_getFetch.promises`
     *
     * @param {String|String[]} path
     * @param {Object} options See options for `StorageService.get`.
     * @return {Promise}
     */
    function getPromise(path, options) {
        return StorageService.get([promisesPath, path], options);
    }

    /**
     * Helper function to shortcut setting values in StorageService's `_getFetch.promises`
     *
     * @param {String|String[]} path
     * @param {Promise} promise
     * @return {Promise}
     */
    function setPromise(path, promise) {
        return StorageService.set([promisesPath, path], promise);
    }

    /**
     * Helper function to shortcut clearing values in StorageService's `_getFetch.promises`
     *
     * @param {String|String[]} path
     * @return {Promise}
     */
    function clearPromise(path) {
        return StorageService.clear([promisesPath, path]);
    }

    return _pub;
}

module.exports = GetFetchService;
